import moment from 'moment';
import { action, computed, makeObservable, observable, override, runInAction } from 'mobx';
import { CHINA_NATIONALITY_VALUE, ONBOARDING_CURRENCY_TYPE, ONBOARDING_STATUSES, ONBOARDING_TYPE } from 'src/constants';
import { ENDPOINT, HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import { ResponseDTO } from 'src/dto/base.dto';
import {
	BorrowerAccountDTO,
	BorrowerEmploymentDetailOnboardingDTO,
	BorrowerFinancialDetailDTO,
	BorrowerOnboardingProgressDTO,
	CreateFunderAccountDetailResponse,
	IBorrowerFinancialResponse,
	IBorrowerOnboardingProgressResponse,
	ICreateFunderAccountRequest,
	OtherDetailDTO,
	SaveDraftDTO,
	SubmitDTO
} from 'src/dto/onboarding.dto';
import {
	IAccountDetail,
	IBorrowerFinancialDetail,
	IEmploymentDetail,
	IFinancialRealEstate,
	IBorrowerCreditHistory,
	IQuestionBorrowerOther,
	IFinancialRegularExpenditure,
	IFinancialYearlyIncome,
	IPersonalBalanceSheet,
	IAccountAddress
} from 'src/interfaces/accounts/account';
import { IHttpService } from 'src/services/http.service';
import { IBaseOnboardingStore, OnboardingStore } from './onboarding.store';
import { convertNumberToStringForObject, convertStringToNumberForObject } from 'src/helpers/common.utils';
import { IUserStore } from '.';

export const EMPLOYMENT_DETAIL_COLUMNS: Array<keyof IEmploymentDetail> = ['annualSalary'];

export const IFinancialRegularExpenditureCols: Array<keyof IFinancialRegularExpenditure> = [
	'annuityRent',
	'household',
	'personalFinanceInstallment',
	'residentialInvestmentMortgage',
	'totalYearlyExpenditures',
	'yearlyDisposableIncome',
	'others'
];
export const IFinancialYearlyIncomeCols: Array<keyof IFinancialYearlyIncome> = [
	'interestDividends',
	'investmentIncome',
	'others',
	'rentalIncome',
	'spouse',
	'totalAnnualIncome',
	'wages'
];
export const IPersonalBalanceSheetCols: Array<keyof IPersonalBalanceSheet> = [
	'bonds',
	'carLoans',
	'deposits',
	'netAssets',
	'netAssets',
	'otherLoans',
	'pensions',
	'personalBalanceSheetMortgage',
	'personalBalanceSheetOther',
	'personalLoans',
	'realEstates',
	'stockFunds',
	'taxLoans',
	'totalAssets',
	'totalLiabilities'
];
export const IFinancialRealEstateCols: Array<keyof IFinancialRealEstate> = [
	'marketValue',
	'mortgageAmount',
	'outstandingLoanAmount'
];

interface IStepValidattion {
	borrowerFinancialDetail?: boolean;
	otherDetails?: boolean;
}

export interface IIndividualBorrowerOnboardingStore extends IBaseOnboardingStore {
	onboardingStatus: ONBOARDING_STATUSES;
	onboardingType: ONBOARDING_TYPE;
	onboardingCurrency: ONBOARDING_CURRENCY_TYPE;
	defaultOnboardingCurrency: ONBOARDING_CURRENCY_TYPE;
	employmentDetail?: IEmploymentDetail;
	borrowerFinancialDetail?: IBorrowerFinancialDetail;
	otherDetails?: IBorrowerCreditHistory;
	defaultOtherDetails?: IBorrowerCreditHistory;
	submittedToKyc?: boolean;
	stepValidation: IStepValidattion;
	saveEmploymentDetail(body: IEmploymentDetail): Promise<boolean>;
	saveFinancialDetail(body: IBorrowerFinancialDetail): Promise<boolean>;
	saveOtherDetail(body: IBorrowerCreditHistory): Promise<boolean>;
	setOnboardingCurrency(currency: ONBOARDING_CURRENCY_TYPE): void;
	get mainRealEstate(): IFinancialRealEstate;
	get disabledSubmitBtn(): boolean;
	get financialRealEstates(): IFinancialRealEstate[];
	get isChineseNationality(): boolean;
	get isAllStepCompleted(): boolean;
	dispose(): void;

	clearOtherInput(type: string): void;

	updateEmploymentType(value: string): void;

	updateEmployerType(value: string): void;

	updateOccupation(value: string): void;

	updateQuestionOtherDetails(value: IQuestionBorrowerOther): void;

	disableButtonSubmit(step: number): boolean;

	downloadIndividualFunderRequestFile(): Promise<void>;
	downloadCorporateFunderRequestFile(): Promise<void>;

	setStepValidation(step: IStepValidattion): void;
}

export class IndividualBorrowerOnboardingStore extends OnboardingStore implements IIndividualBorrowerOnboardingStore {
	onboardingStatus: ONBOARDING_STATUSES = ONBOARDING_STATUSES.InCompleted;
	onboardingType = ONBOARDING_TYPE.BORROWER;
	onboardingCurrency = ONBOARDING_CURRENCY_TYPE.HKD;
	defaultOnboardingCurrency = ONBOARDING_CURRENCY_TYPE.HKD;
	employmentDetail?: IEmploymentDetail = {};
	borrowerFinancialDetail?: IBorrowerFinancialDetail;
	otherDetails?: IBorrowerCreditHistory = {};
	defaultOtherDetails?: IBorrowerCreditHistory = {};

	submittedToKyc?: boolean = false;
	stepValidation: IStepValidattion = {
		borrowerFinancialDetail: false,
		otherDetails: false
	}

	constructor(protected readonly http: IHttpService, private userStore: IUserStore) {
		super(http);
		makeObservable(this, {
			employmentDetail: observable.shallow,
			onboardingType: observable,
			onboardingCurrency: observable,
			borrowerFinancialDetail: observable,
			setOnboardingCurrency: action.bound,
			loadProgress: override,
			mainRealEstate: computed,
			financialRealEstates: computed,
			isChineseNationality: computed,
			saveOtherDetail: action,
			dispose: action,
			clearOtherInput: action.bound,
			updateEmploymentType: action.bound,
			updateEmployerType: action.bound,
			updateOccupation: action.bound,
			updateQuestionOtherDetails: action.bound,
			otherDetails: observable.shallow,
			defaultOtherDetails: observable.shallow,
			setStepValidation: action.bound,
			stepValidation: observable,
			isAllStepCompleted: computed
		});
	}

	get mainRealEstate(): IFinancialRealEstate {
		if (!this.borrowerFinancialDetail || !this.borrowerFinancialDetail.financialRealEstates) {
			return {};
		}
		return this.borrowerFinancialDetail.financialRealEstates[0];
	}
	get financialRealEstates(): IFinancialRealEstate[] {
		if (!this.borrowerFinancialDetail || !this.borrowerFinancialDetail.financialRealEstates) {
			return [];
		}
		return this.borrowerFinancialDetail.financialRealEstates.slice(1) || [];
	}

	get isChineseNationality(): boolean {
		return this.accountDetail?.nationality === CHINA_NATIONALITY_VALUE;
	}

	setOnboardingCurrency(currency: ONBOARDING_CURRENCY_TYPE): void {
		this.onboardingCurrency = currency;
	}

	async saveEmploymentDetail(body: IEmploymentDetail): Promise<boolean> {
		body = convertStringToNumberForObject<IEmploymentDetail>(body, EMPLOYMENT_DETAIL_COLUMNS);
		const dto = new BorrowerEmploymentDetailOnboardingDTO({
			...body,
			personId: this.accountDetail?.personId
		});

		const result: ResponseDTO<string> = await this.http.request<BorrowerEmploymentDetailOnboardingDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.employmentDetail = {
					...convertNumberToStringForObject<IEmploymentDetail>(body, EMPLOYMENT_DETAIL_COLUMNS),
					id: result.data
				};
			});
			return true;
		}
		return false;
	}

	async saveDraft(): Promise<boolean> {
		const dto = new SaveDraftDTO(ENDPOINT.BORROWER_ONBOARDING_DAVE_DRAFT);
		const result: ResponseDTO<string> = await this.http.request<SaveDraftDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStatus = ONBOARDING_STATUSES.Draft;
			});
			return true;
		}
		return false;
	}

	async submit(): Promise<boolean> {
		const dto = new SubmitDTO(ENDPOINT.BORROWER_ONBOARDING_SUBMIT);
		const result: ResponseDTO<string> = await this.http.request<SubmitDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStatus = ONBOARDING_STATUSES.WaitingForApproval;
			});
			return true;
		}
		return false;
	}

	async loadProgress(): Promise<boolean> {
		const dto = new BorrowerOnboardingProgressDTO();
		const result = await this.http.request<BorrowerOnboardingProgressDTO, IBorrowerOnboardingProgressResponse>(dto);
		if (result.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.accountDetail = result.data?.accountDetail;
				if (result.data && result.data.borrowerEmploymentDetailResponse) {
					this.employmentDetail = {
						...convertNumberToStringForObject<IEmploymentDetail>(
							result.data.borrowerEmploymentDetailResponse,
							EMPLOYMENT_DETAIL_COLUMNS
						)
					};
				}

				if (this.employmentDetail) {
					this.onboardingCurrency = this.employmentDetail.currency as ONBOARDING_CURRENCY_TYPE;
					this.onboardingStatus = ONBOARDING_STATUSES.Draft;
				}
				if (result.data && result.data.borrowerFinancialDetailResponse) {
					this.borrowerFinancialDetail = {
						financialRegularExpenditure: convertNumberToStringForObject<IFinancialRegularExpenditure>(
							result.data.borrowerFinancialDetailResponse.financialRegularExpenditure || {},
							IFinancialRegularExpenditureCols
						),
						personalBalanceSheet: convertNumberToStringForObject<IPersonalBalanceSheet>(
							result.data.borrowerFinancialDetailResponse.personalBalanceSheet || {},
							IPersonalBalanceSheetCols
						),
						financialYearlyIncome: convertNumberToStringForObject<IFinancialYearlyIncome>(
							result.data.borrowerFinancialDetailResponse.financialYearlyIncome || {},
							IFinancialYearlyIncomeCols
						),
						currency: result.data.borrowerFinancialDetailResponse.currency || ONBOARDING_CURRENCY_TYPE.HKD
					};
					if (
						result.data.borrowerFinancialDetailResponse.financialRealEstates &&
						result.data.borrowerFinancialDetailResponse.financialRealEstates.length
					) {
						this.borrowerFinancialDetail.financialRealEstates =
							result.data.borrowerFinancialDetailResponse.financialRealEstates.map((m) => {
								return convertNumberToStringForObject<IFinancialRealEstate>(m, IFinancialRealEstateCols);
							});
					}

					this.onboardingCurrency = result.data.referenceCurrency as ONBOARDING_CURRENCY_TYPE;
					this.defaultOnboardingCurrency = this.onboardingCurrency;
				}
				if (result.data?.otherDetailResponse) {
					this.otherDetails = {
						...result.data.otherDetailResponse,
						questions: JSON.parse(result.data.otherDetailResponse.questions as string)
					};

					this.defaultOtherDetails = { ...this.otherDetails };
				}

				this.supportingDocuments = result.data?.supportingDocuments;
				this.submittedToKyc = result.data?.submittedToKyc;

				if (this.accountDetail?.dob) {
					this.accountDetail.dob = moment(this.accountDetail.dob);
				}

				this.setBorrowerProgressLoaded();

				if(result.data?.accountDetail?.personId) {
					this.userStore.setPersonId(result.data?.accountDetail?.personId)
				}
			});
			return true;
		}
		return false;
	}

	public async saveAccountDetail(body: IAccountDetail): Promise<boolean> {
		const em = convertStringToNumberForObject<IEmploymentDetail>(body.employmentDetail || {}, ['annualSalary']);
		const bodyDto: ICreateFunderAccountRequest = {
			...body,
			personId: this.accountDetail?.personId,
			dateOfBirth: body.dob?.toDate(),
			personalAccountType: this.onboardingType,
			employmentDetail: em,
			residentialAddress: body.residentialAddress as IAccountAddress
		};
		if (body.mailingAddress) {
			bodyDto.mailingAddress = body.mailingAddress as IAccountAddress
		}
		const dto = new BorrowerAccountDTO(bodyDto);
		const result: ResponseDTO<CreateFunderAccountDetailResponse> = await this.http.request<
			BorrowerAccountDTO,
			CreateFunderAccountDetailResponse
		>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.accountDetail = {
					...this.accountDetail,
					...body,
					personId: result.data?.personId,
					employmentDetail: convertNumberToStringForObject<IEmploymentDetail>(em, ['annualSalary'])
				};
			});
			return true;
		}
		return false;
	}

	public async saveFinancialDetail(
		body: IBorrowerFinancialDetail & { mainRealEstate: IFinancialRealEstate }
	): Promise<boolean> {
		const requestBody: IBorrowerFinancialDetail = {
			currency: body.currency,
			financialRegularExpenditure: convertStringToNumberForObject<IFinancialRegularExpenditure>(
				body.financialRegularExpenditure || {},
				IFinancialRegularExpenditureCols
			),
			financialYearlyIncome: convertStringToNumberForObject<IFinancialYearlyIncome>(
				body.financialYearlyIncome || {},
				IFinancialYearlyIncomeCols
			),
			personalBalanceSheet: convertStringToNumberForObject<IPersonalBalanceSheet>(
				body.personalBalanceSheet || {},
				IPersonalBalanceSheetCols
			),
			financialRealEstates: [body.mainRealEstate, ...(body.financialRealEstates || [])],
			personId: this.accountDetail?.personId
		};
		requestBody.financialRealEstates = [
			convertStringToNumberForObject<IFinancialRealEstate>(body.mainRealEstate, IFinancialRealEstateCols)
		];

		const requestRealEstate = body.financialRealEstates || [];
		requestRealEstate.forEach((el) => {
			requestBody.financialRealEstates?.push(
				convertStringToNumberForObject<IFinancialRealEstate>(
					{
						id: el.id,
						address: el.address,
						country: el.country,
						district: el.district,
						marketValue: el.marketValue,
						mortgageAmount: el.marketValue,
						outstandingLoanAmount: el.outstandingLoanAmount,
						zipCode: el.zipCode,
						street: el.street
					},
					IFinancialRealEstateCols
				)
			);
		});
		const dto = new BorrowerFinancialDetailDTO(requestBody);

		const result: ResponseDTO<IBorrowerFinancialResponse> = await this.http.request<
			BorrowerFinancialDetailDTO,
			IBorrowerFinancialResponse
		>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.borrowerFinancialDetail = {
					...body,
					financialRealEstates: [body.mainRealEstate, ...(body.financialRealEstates || [])]
				};
				this.borrowerFinancialDetail.personId = result.data?.personId;
				if (this.borrowerFinancialDetail.financialYearlyIncome) {
					this.borrowerFinancialDetail.financialYearlyIncome.id = result.data?.financialYearlyIncomeId;
				}
				if (this.borrowerFinancialDetail.financialRegularExpenditure) {
					this.borrowerFinancialDetail.financialRegularExpenditure.id = result.data?.financialRegularExpenditureId;
				}
				if (this.borrowerFinancialDetail.personalBalanceSheet) {
					this.borrowerFinancialDetail.personalBalanceSheet.id = result.data?.personalBalanceSheetId;
				}
				this.onboardingCurrency = body.currency as ONBOARDING_CURRENCY_TYPE;
				this.defaultOnboardingCurrency = body.currency as ONBOARDING_CURRENCY_TYPE;

			});
			return true;
		}
		return false;
	}

	public async saveOtherDetail(body: IBorrowerCreditHistory): Promise<boolean> {
		const requestBody = {
			personId: this.accountDetail?.personId,
			questions: JSON.stringify(body.questions)
		};

		const dto = new OtherDetailDTO(requestBody);
		const result: ResponseDTO<string> = await this.http.request<OtherDetailDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.otherDetails = {
					...body,
					id: result.data
				};
				this.defaultOtherDetails = { ...this.otherDetails }
			});
			return true;
		}
		return false;
	}

	public dispose(): void {
		this.borrowerOnboardingRequest = undefined;
		this.funderOnboardingRequest = undefined;
		this.employmentDetail = undefined;
		this.borrowerFinancialDetail = undefined;
		this.otherDetails = undefined;
		this.defaultOtherDetails = undefined;
	}

	public clearOtherInput(type: string): void {
		if (type === 'employmentTypeOther') {
			this.employmentDetail = {
				...this.employmentDetail,
				employmentTypeOther: undefined
			};
		}

		if (type === 'employerTypeOther') {
			this.employmentDetail = {
				...this.employmentDetail,
				employerTypeOther: undefined
			};
		}

		if (type === 'occupationOther') {
			this.employmentDetail = {
				...this.employmentDetail,
				occupationOther: undefined
			};
		}
	}

	public updateEmploymentType(value: string): void {
		this.employmentDetail = {
			...this.employmentDetail,
			employmentType: value
		};
	}

	public updateEmployerType(value: string): void {
		this.employmentDetail = {
			...this.employmentDetail,
			employerType: value
		};
	}

	public updateOccupation(value: string): void {
		this.employmentDetail = {
			...this.employmentDetail,
			occupation: value
		};
	}

	public updateQuestionOtherDetails(newValue: IQuestionBorrowerOther): void {
		this.otherDetails = {
			...this.otherDetails,
			questions: newValue && Object.keys(newValue).reduce((acc, ele: string) => {
				acc = {
					...acc,
					[ele]: {
						value: newValue[ele as keyof typeof newValue]?.value,
						detail: newValue[ele as keyof typeof newValue]?.value ?
							newValue[ele as keyof typeof newValue]?.detail : undefined
					}
				}
				return acc;
			}, {})
		};
	}

	public disableButtonSubmit(step: number): boolean {
		if (step === 2) return false;
		if (this.onboardingStatus === ONBOARDING_STATUSES.Draft) {
			const isUploadSupportingDocuments =
				// Number(this.supportingDocuments?.bankCardImg?.length) > 0 &&
				Number(this.supportingDocuments?.bankStatementDocument?.length) > 0 &&
				Number(this.supportingDocuments?.contractExchangeDocument?.length) > 0 &&
				Number(this.supportingDocuments?.energyPerformanceDocument?.length) > 0 &&
				Number(this.supportingDocuments?.ews1FormDocument?.length) > 0 &&
				Number(this.supportingDocuments?.existingMortgageStatementDocument?.length) > 0 &&
				Number(this.supportingDocuments?.existingTenancyDocument?.length) > 0 &&
				Number(this.supportingDocuments?.identities?.length) > 0 &&
				Number(this.supportingDocuments?.proofIncome?.length) > 0 &&
				Number(this.supportingDocuments?.proofOfDepositDocument?.length) > 0 &&
				Number(this.supportingDocuments?.reportOnTitleDocument?.length) > 0 &&
				Number(this.supportingDocuments?.reservationDocument?.length) > 0 &&
				Number(this.supportingDocuments?.residentialProof?.length) > 0 &&
				Number(this.supportingDocuments?.selfie?.length) > 0 &&
				Number(this.supportingDocuments?.surveyorDocument?.length) > 0 &&
				Number(this.supportingDocuments?.titleFloorDocument?.length) > 0 &&
				Number(this.supportingDocuments?.valuationDocument?.length) > 0 &&
				Number(this.supportingDocuments?.ukFinanceDocument?.length) > 0;

			if (isUploadSupportingDocuments) {
				return false;
			}

			return true;
		}
		return true;
	}

	public setStepValidation(step: IStepValidattion): void {
		this.stepValidation = step;
	}

	get disabledSubmitBtn(): boolean {
		const isAlwayRequiredDocs: boolean = (this.supportingDocuments &&
			(this.supportingDocuments.bankStatementDocument || []).length > 0 &&
			(this.supportingDocuments.proofIncome || []).length > 0 &&
			(this.supportingDocuments.residentialProof || []).length > 0 &&
			(this.supportingDocuments.selfie || []).length > 0) as boolean;

		const isRequiredDocsByNationality: boolean = (this.supportingDocuments &&
			(this.accountDetail?.nationality === CHINA_NATIONALITY_VALUE
				? (this.supportingDocuments.chineseIdImgFront || []).length > 0 &&
				(this.supportingDocuments.chineseIdImgBack || []).length > 0 &&
				(this.supportingDocuments.bankCardImg || []).length > 0
				:
				(this.supportingDocuments.identities || []).length > 0)) as boolean;

		// const isAllStepCompleted = Object.values(this.stepValidation).every(item => Boolean(item));

		if (isAlwayRequiredDocs && isRequiredDocsByNationality) return false;
		return true;
	}

	get isAllStepCompleted(): boolean {
		return Object.values(this.stepValidation).every(item => Boolean(item));
	}

	public async downloadIndividualFunderRequestFile(): Promise<void> {
		await this.http.downloadFile(
			ENDPOINT.DOWNLOAD_INDIVIDUAL_BORROWER_REQUEST_FILE,
			{},
			{
				name: 'BGR.pdf'
			}
		);
	}

	public async downloadCorporateFunderRequestFile(): Promise<void> {
		await this.http.downloadFile(
			ENDPOINT.DOWNLOAD_CORPORATE_BORROWER_REQUEST_FILE,
			{},
			{
				name: 'BGR.pdf'
			}
		);
	}
}
